import React from "react";
import PropTypes from "prop-types";
import { useSelector, useDispatch } from "react-redux";
import styled from "styled-components";
import { Link } from "react-router-dom";
import routes from "../routes/routes";

// redux actions
import { update as updateShowSpinner } from "../store/slices/showSpinner";

// components
import Button from "../components/Button";
import PageWrap from "../components/PageWrap";
import SkeletonBox from "../components/SkeletonBox";
import HalfHeight from "../components/HalfHeight";

// images
const mitekIdFrontPortraitAuto =
  process.env.PUBLIC_URL + "/static/images/mitek-id-front-portrait-auto.gif";
const mitekIdFrontLandscapeAuto =
  process.env.PUBLIC_URL + "/static/images/mitek-id-front-landscape-auto.gif";
const mitekBarcodePortraitAuto =
  process.env.PUBLIC_URL + "/static/images/mitek-barcode-portrait-auto.gif";
const mitekBarcodeLandscapeAuto =
  process.env.PUBLIC_URL + "/static/images/mitek-barcode-landscape-auto.gif";
const mitekIdFrontPortraitManual =
  process.env.PUBLIC_URL + "/static/images/mitek-id-front-portrait-manual.gif";
const mitekIdFrontLandscapeManual =
  process.env.PUBLIC_URL + "/static/images/mitek-id-front-landscape-manual.gif";
const mitekBarcodePortraitManual =
  process.env.PUBLIC_URL + "/static/images/mitek-barcode-portrait-manual.gif";
const mitekBarcodeLandscapeManual =
  process.env.PUBLIC_URL + "/static/images/mitek-barcode-landscape-manual.gif";
const mitekPassport =
  process.env.PUBLIC_URL + "/static/images/mitek-passport.gif";

const StyledList = styled.ul`
  text-align: left;
  font-size: 1.125em;
  list-style-type: none;
  padding: 0;
  & li {
    margin-bottom: 0.9em;
  }
`;

const StyledImage = styled.img`
  max-width: 100%;
  display: block;
  max-height: 90%;
`;

const CheckIcon = styled.img`
  display: inline-block;
  width: 16px;
  height: 12px;
  margin-right: 0.44em;
`;

const StyledDiv = styled.div`
  padding: 2em 1em 0;
`;

const StyledPageWrap = styled(PageWrap)`
  padding-left: 0;
  padding-right: 0;
`;

const StyledStrong = styled.strong`
  font-size: 1.125em;
`;

const StyledHeading = styled.h2`
  padding-left: 0.45em;
  font-size: 1.25em;
  font-weight: normal;
`;

function Instructions({ startCaptureSession }) {
  /**
   * TODO: track the orientation of the browser whether it's portait
   * or landscape so we can display the right picture
   */
  const orientation = "portrait-primary";

  // Redux values
  const currentlyCapturing = useSelector(
    (state) => state.currentlyCapturing.value,
  );
  const mode = useSelector((state) => state.mode.value);

  // Redux actions
  const dispatch = useDispatch();

  // show spinner and activate camera
  const handleClick = () => {
    dispatch(updateShowSpinner(true));
    startCaptureSession();
  };

  // return various text / jsx for page that's context dependent
  const content = (section) => {
    // front of driver's license content
    if (currentlyCapturing === "driver's license front") {
      if (section === "step") {
        return "1 of 3";
      }
      if (section === "title") {
        return (
          <StyledHeading>
            Capture <strong>Front</strong> of Driver's License
          </StyledHeading>
        );
      }
      if (section === "image") {
        if (
          orientation === "portrait-primary" ||
          orientation === "portrait-secondary"
        ) {
          if (mode === "AUTO_CAPTURE") {
            return mitekIdFrontPortraitAuto;
          }
          return mitekIdFrontPortraitManual;
        }
        if (mode === "AUTO_CAPTURE") {
          return mitekIdFrontLandscapeAuto;
        }
        return mitekIdFrontLandscapeManual;
      }
    }
    // back of driver's license content
    if (currentlyCapturing === "driver's license back") {
      if (section === "step") {
        return "2 of 3";
      }
      if (section === "title") {
        return (
          <StyledHeading>
            Capture <strong>Back</strong> of Driver's License
          </StyledHeading>
        );
      }
      if (section === "image") {
        if (
          orientation === "portrait-primary" ||
          orientation === "portrait-secondary"
        ) {
          if (mode === "AUTO_CAPTURE") {
            return mitekBarcodePortraitAuto;
          }
          return mitekBarcodePortraitManual;
        }
        if (mode === "AUTO_CAPTURE") {
          return mitekBarcodeLandscapeAuto;
        }
        return mitekBarcodeLandscapeManual;
      }
    }
    // passport content
    if (currentlyCapturing === "passport") {
      if (section === "step") {
        return "1 of 2";
      }
      if (section === "title") {
        return (
          <StyledHeading>
            Capture <strong>Passport</strong>
          </StyledHeading>
        );
      }
      if (section === "image") {
        return mitekPassport;
      }
    }
  };

  return (
    <StyledPageWrap noSpacer noHeader>
      <Button back as={Link} to={`${routes.chooseDocument}`} />
      <HalfHeight blue>
        <SkeletonBox>
          <StyledImage
            src={content("image")}
            alt="capture document animation"
          />
        </SkeletonBox>
      </HalfHeight>
      <HalfHeight>
        <StyledDiv>
          <SkeletonBox>
            <StyledStrong>Step {content("step")}</StyledStrong>
          </SkeletonBox>
          <SkeletonBox>{content("title")}</SkeletonBox>
          <StyledList>
            <SkeletonBox>
              <li>
                <CheckIcon
                  alt=""
                  src="/static/images/iconCheckmarkCompletedTask.svg"
                />
                Use a dark background
              </li>
            </SkeletonBox>
            <SkeletonBox>
              <li>
                <CheckIcon
                  alt=""
                  src="/static/images/iconCheckmarkCompletedTask.svg"
                />
                Capture all 4 corners in the frame
              </li>
            </SkeletonBox>
            <SkeletonBox>
              <li>
                <CheckIcon
                  alt=""
                  src="/static/images/iconCheckmarkCompletedTask.svg"
                />
                Ensure that the lighting is bright
              </li>
            </SkeletonBox>
          </StyledList>
        </StyledDiv>
        <StyledDiv>
          <Button type="button" onClick={handleClick}>
            Continue
          </Button>
        </StyledDiv>
      </HalfHeight>
    </StyledPageWrap>
  );
}

Instructions.propTypes = {
  startCaptureSession: PropTypes.func,
};

export default Instructions;
